import React from 'react';
import { PiCaretRight } from 'react-icons/pi';

import { Checkbox } from 'antd';
import Chip from 'components/common/Chip/Chip';
import { STEP_SELECTOR_SEED_CHANGE_VARIETY_LABEL } from 'constants/uiConstants';
import { GcVariety } from 'interfaces/IGlobalCatalogue';

import OptionRow from './OptionRow';
import SeedVarietyFlyout from './SeedVarietyFlyout';
import {
  getSeedOptionDisplayLabel,
  getStepOptionId,
  PICKER_OPTION_ID_ATTRIBUTE,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';

interface SeedCropVarietyPickerProps {
  crops: StepSelectorOption[];
  isMultiSelectMode: boolean;
  checkedItems: StepSelectorOption[];
  highlightedCrop: StepSelectorOption | null;
  isFlyoutOpen: boolean;
  highlightedVariety: GcVariety | null;
  varietySearchText: string;
  decideLaterLabel: string;
  anchorRef: React.RefObject<HTMLElement>;
  onCropClick: (crop: StepSelectorOption, event: React.MouseEvent) => void;
  onChangeVariety: (crop: StepSelectorOption) => void;
  onVarietySelect: (variety: GcVariety, event?: React.MouseEvent) => void;
  onDecideLater: (event?: React.MouseEvent) => void;
  onVarietySearchChange: (value: string) => void;
  onCloseFlyout: () => void;
}

/**
 * Seed crop list, styled like every other group's plain list.
 * - Clicking a crop opens SeedVarietyFlyout beside the picker; picking a variety finishes the pair.
 * - Multi-select: clicking an unchecked crop checks it and opens the flyout; clicking a
 *   checked crop unchecks it; the Change Variety chip reopens the flyout.
 * - Fully controlled by StepInputSelector so useStepSelectorKeyboard can drive it.
 */
const SeedCropVarietyPicker: React.FC<SeedCropVarietyPickerProps> = ({
  crops,
  isMultiSelectMode,
  checkedItems,
  highlightedCrop,
  isFlyoutOpen,
  highlightedVariety,
  varietySearchText,
  decideLaterLabel,
  anchorRef,
  onCropClick,
  onChangeVariety,
  onVarietySelect,
  onDecideLater,
  onVarietySearchChange,
  onCloseFlyout,
}) => (
  <>
    {crops.map((crop) => {
      const checkedEntry = checkedItems.find((checked) =>
        stepOptionsMatch(checked, crop)
      );
      const isHighlighted =
        !!highlightedCrop && stepOptionsMatch(highlightedCrop, crop);
      const isFilled = !!checkedEntry || isHighlighted;
      const metaOption = checkedEntry ?? crop;

      return (
        <OptionRow
          key={`${crop.item}-${crop.globalId ?? 'none'}`}
          navAttributes={{
            [PICKER_OPTION_ID_ATTRIBUTE]: getStepOptionId(crop),
          }}
          label={
            checkedEntry ? getSeedOptionDisplayLabel(checkedEntry) : crop.label
          }
          metaOption={metaOption}
          isHighlighted={isHighlighted}
          isFilled={isFilled}
          onClick={(event) => onCropClick(crop, event)}
          leading={
            isMultiSelectMode && (
              <Checkbox
                checked={!!checkedEntry}
                className="mt-1"
                onClick={(event) => {
                  event.stopPropagation();
                  onCropClick(crop, event);
                }}
              />
            )
          }
          trailing={
            isMultiSelectMode && checkedEntry ? (
              <span className="flex-shrink-0 self-center">
                <Chip
                  id={`change-variety-${getStepOptionId(crop)}`}
                  label={STEP_SELECTOR_SEED_CHANGE_VARIETY_LABEL}
                  onClick={() => onChangeVariety(crop)}
                />
              </span>
            ) : (
              <PiCaretRight
                size={14}
                className="flex-shrink-0 self-center text-gray-medium"
                aria-hidden="true"
              />
            )
          }
        />
      );
    })}

    {isFlyoutOpen && highlightedCrop && (
      <SeedVarietyFlyout
        key={highlightedCrop.globalId ?? highlightedCrop.item}
        anchorRef={anchorRef}
        cropLabel={highlightedCrop.label}
        varieties={highlightedCrop.varieties ?? []}
        highlightedVariety={highlightedVariety}
        searchText={varietySearchText}
        decideLaterLabel={decideLaterLabel}
        onSearchChange={onVarietySearchChange}
        onSelectVariety={onVarietySelect}
        onDecideLater={onDecideLater}
        onClose={onCloseFlyout}
      />
    )}
  </>
);

export default SeedCropVarietyPicker;
